// src/common/security/capyClient.js
// v2 checkbox reCAPTCHA için güvenli yükleme + render.
// Ortama göre (domain) doğru site key seçer. getResponse/reset widgetId ile çalışır.

let scriptLoading = null;
let scriptAdded = false;
let onloadResolvers = [];

/* =========================
   1) SITE KEY SEÇİMİ (Prod/Dev)
   =========================
   .env içine şunları eklemen önerilir:
   - REACT_APP_RECAPTCHA_SITE_KEY_PROD=.....   // prod domain(ler) için
   - REACT_APP_RECAPTCHA_SITE_KEY_DEV=.....    // localhost/127.0.0.1 için
   (İstersen tek anahtar kullan: REACT_APP_RECAPTCHA_SITE_KEY=.....)

   Not: Google reCAPTCHA konsolunda domain whitelist AYNI anahtar için doğru girilmiş olmalı.
*/
const SITE_KEY_EXPLICIT = process.env.REACT_APP_RECAPTCHA_SITE_KEY || ""; // tek anahtar kullanırsan
const SITE_KEY_PROD = process.env.REACT_APP_RECAPTCHA_SITE_KEY_PROD || "";
const SITE_KEY_DEV  = process.env.REACT_APP_RECAPTCHA_SITE_KEY_DEV  || "";

function pickSiteKeyByHost(hostname) {
  const host = (hostname || "").toLowerCase();
  const isLocal = (
    host === "localhost" ||
    host === "127.0.0.1" ||
    host.endsWith(".local")
  );

  // 1) Tek anahtar verilmişse onu kullan
  if (SITE_KEY_EXPLICIT) {
    return { siteKey: SITE_KEY_EXPLICIT, env: "explicit" };
  }

  // 2) Prod/Dev ayrımı
  if (isLocal && SITE_KEY_DEV) {
    return { siteKey: SITE_KEY_DEV, env: "dev" };
  }
  if (!isLocal && SITE_KEY_PROD) {
    return { siteKey: SITE_KEY_PROD, env: "prod" };
  }

  // 3) Hiçbiri yoksa uyarı + fallback (eski sabitini kullanıyordun, oraya düşürmek istersen buraya koy)
  // !!! Bu fallback sadece geliştirme kolaylığı içindir; prod'da doğru .env girin.
  const FALLBACK = "6LcUDAktAAAAAHr23iyvDmCn6jicDzALSvn2plYx";
  console.warn("[reCAPTCHA] Site key env bulunamadı, FALLBACK anahtarı kullanılıyor. Prod için .env ayarlayın.");
  return { siteKey: FALLBACK, env: "fallback" };
}

/* =========================
   2) SCRIPT YÜKLEME
   ========================= */
function ensureScript() {
  if (scriptLoading) return scriptLoading;

  scriptLoading = new Promise((resolve, reject) => {
    // zaten yüklüyse
    if (window.grecaptcha && typeof window.grecaptcha.render === "function") {
      resolve(window.grecaptcha);
      return;
    }

    if (!scriptAdded) {
      scriptAdded = true;
      const s = document.createElement("script");
      // v2 checkbox için doğru script
      s.src = "https://www.google.com/recaptcha/api.js?onload=__recaptchaOnload&render=explicit&hl=tr";
      s.async = true;
      s.defer = true;
      s.onerror = () => reject(new Error("reCAPTCHA script load error"));
      document.head.appendChild(s);

      window.__recaptchaOnload = () => {
        // render hazır olmadan resolve etme
        if (window.grecaptcha && typeof window.grecaptcha.render === "function") {
          onloadResolvers.forEach((r) => r(window.grecaptcha));
          onloadResolvers = [];
        }
      };
    }

    onloadResolvers.push(resolve);
  });

  return scriptLoading;
}

/* =========================
   3) RENDER / RESET / RESPONSE
   ========================= */
export async function renderRecaptcha(containerId, callbacks = {}) {
  const { siteKey, env } = pickSiteKeyByHost(window.location.hostname);
  const grecaptcha = await ensureScript();

  // container
  const el = document.getElementById(containerId);
  if (!el) throw new Error("reCAPTCHA container bulunamadı: " + containerId);
  if (el.childElementCount > 0) el.innerHTML = ""; // tekrarda sıfırla

  // render
  try {
    const widgetId = grecaptcha.render(containerId, {
      sitekey: siteKey,
      theme: "light",
      size: "normal",
      callback: (token) => { typeof callbacks.callback === "function" && callbacks.callback(token); },
      "expired-callback": () => { typeof callbacks["expired-callback"] === "function" && callbacks["expired-callback"](); },
      "error-callback": () => { typeof callbacks["error-callback"] === "function" && callbacks["error-callback"](); },
    });

    // Debug bilgisi (konsolda hangi key yolu ile geldiğini görebilirsin)
    if (process.env.NODE_ENV !== "production") {
      console.info(`[reCAPTCHA] widgetId=${widgetId} | mode=${env} | host=${window.location.hostname}`);
    }

    return widgetId;
  } catch (err) {
    // Buraya genelde "geçersiz alan" gibi problemler düşer.
    console.error("[reCAPTCHA] render error:", err);
    throw err;
  }
}

export function resetRecaptcha(widgetId) {
  if (window.grecaptcha && typeof window.grecaptcha.reset === "function") {
    window.grecaptcha.reset(widgetId);
  }
}

export function getResponse(widgetId) {
  if (window.grecaptcha && typeof window.grecaptcha.getResponse === "function") {
    return window.grecaptcha.getResponse(widgetId);
  }
  return "";
}
